import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Flag, Loader2, AlertCircle, RefreshCw, Check, X,
  CheckCircle2, XCircle, Clock
} from 'lucide-react';
import AdminNav from './AdminNav';
import AdminGate from './AdminGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASON_LABELS = {
  invalid_phone: 'Invalid phone',
  duplicate: 'Duplicate',
  unreachable: 'Unreachable',
  wrong_service: 'Wrong service',
  spam: 'Spam',
};

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' },
];

const AdminLeadReportsInner = ({ adminEmail }) => {
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [error, setError] = useState('');
  const [adminNote, setAdminNote] = useState({});

  const fetchReports = useCallback(async (status) => {
    setLoading(true);
    setError('');
    try {
      const url = `${API}/admin/lead-reports${status ? `?status=${status}` : ''}`;
      const res = await axios.get(url, { withCredentials: true });
      setReports(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load lead reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(statusFilter); }, [fetchReports, statusFilter]);

  const handleResolve = async (report, action) => {
    setResolving(`${report.id}:${action}`);
    setError('');
    try {
      await axios.put(
        `${API}/admin/lead-reports/${report.id}/${action}`,
        { admin_note: adminNote[report.id] || '' },
        { withCredentials: true }
      );
      await fetchReports(statusFilter);
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to ${action} report`);
    } finally {
      setResolving(null);
    }
  };

  const counts = reports.reduce(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }),
    {}
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav activeEmail={adminEmail} />
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#37b34a]/10 rounded-xl flex items-center justify-center">
              <Flag size={18} className="text-[#37b34a]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Lead Issue Reports</h1>
              <p className="text-xs text-gray-500">Review reports & refund credits</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchReports(statusFilter)}
              data-testid="reports-refresh-btn"
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="reports-filter-chips">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value || 'all'}
              onClick={() => setStatusFilter(f.value)}
              data-testid={`reports-filter-${f.value || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                statusFilter === f.value
                  ? 'bg-[#37b34a] text-white border-[#37b34a]'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {f.label}
              {statusFilter === f.value && f.value && counts[f.value] !== undefined && (
                <span className="ml-1.5 opacity-80">({counts[f.value]})</span>
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-lg flex items-center gap-2 mb-4">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {loading && reports.length === 0 ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : reports.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-12 text-center">
            <Flag size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No reports {statusFilter ? `with status "${statusFilter}"` : ''}.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-reports-list">
            {reports.map(r => (
              <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold border border-red-200">
                        <Flag size={10} /> {REASON_LABELS[r.reason] || r.reason}
                      </span>
                      {r.status === 'pending' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200">
                          <Clock size={10} /> Pending
                        </span>
                      )}
                      {r.status === 'approved' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-200">
                          <CheckCircle2 size={10} /> Approved · refunded {r.refund_amount ?? Math.round(r.credits_used)} credits
                        </span>
                      )}
                      {r.status === 'rejected' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 text-xs font-semibold border border-gray-200">
                          <XCircle size={10} /> Rejected
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">{r.service_name || '—'}</h3>
                  </div>
                  <span className="text-xs text-gray-400">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 text-sm mb-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs uppercase font-semibold text-gray-500 mb-1">Reporting Pro</p>
                    <p className="text-gray-900 font-medium">{r.professional_name || '—'}</p>
                    <p className="text-xs text-gray-500">{r.professional_phone || '—'}</p>
                    <p className="text-xs text-gray-500">{r.professional_email || '—'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs uppercase font-semibold text-gray-500 mb-1">Customer (lead)</p>
                    <p className="text-gray-900 font-medium">{r.customer_name || '—'}</p>
                    <p className="text-xs text-gray-500">{r.customer_phone || '—'}</p>
                    <p className="text-xs text-gray-500">{r.lead_location || '—'}</p>
                  </div>
                </div>

                {r.lead_description && (
                  <div className="bg-blue-50/40 rounded-lg p-3 mb-3">
                    <p className="text-xs uppercase font-semibold text-gray-500 mb-1">Lead description</p>
                    <p className="text-sm text-gray-800">{r.lead_description}</p>
                  </div>
                )}

                {r.notes && (
                  <div className="bg-amber-50/40 rounded-lg p-3 mb-3">
                    <p className="text-xs uppercase font-semibold text-gray-500 mb-1">Pro's notes</p>
                    <p className="text-sm text-gray-800 whitespace-pre-line">{r.notes}</p>
                  </div>
                )}

                <div className="text-xs text-gray-500 mb-3">
                  Credits at risk: <span className="font-semibold text-gray-900">{Math.round(r.credits_used)}</span>
                </div>

                {r.status === 'pending' ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Optional admin note (visible to pro)"
                      value={adminNote[r.id] || ''}
                      onChange={(e) => setAdminNote(prev => ({ ...prev, [r.id]: e.target.value }))}
                      maxLength={500}
                      data-testid={`admin-note-input-${r.id}`}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#37b34a]"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleResolve(r, 'approve')}
                        disabled={!!resolving}
                        data-testid={`approve-report-${r.id}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold text-white bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 transition-colors"
                      >
                        {resolving === `${r.id}:approve` ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Check size={14} />
                        )}
                        Approve & Refund
                      </button>
                      <button
                        onClick={() => handleResolve(r, 'reject')}
                        disabled={!!resolving}
                        data-testid={`reject-report-${r.id}`}
                        className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
                      >
                        {resolving === `${r.id}:reject` ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <X size={14} />
                        )}
                        Reject
                      </button>
                    </div>
                  </div>
                ) : (
                  r.admin_note && (
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <span className="font-semibold">Admin note:</span> {r.admin_note}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const AdminLeadReports = () => (
  <AdminGate>
    {({ adminEmail }) => <AdminLeadReportsInner adminEmail={adminEmail} />}
  </AdminGate>
);

export default AdminLeadReports;
